import { IconButton, SettingsIcon } from "@minervaproject/fds";

import { Menu } from "components/header/menu";
import { MenuItem, MenuItemHeader } from "components/header/menu_item";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import {
  setEnableStudentDrawing,
  setUserWhiteboardFontSizePreference,
  setUserWhiteboardStrokeSizePreference,
  setUserZoomPreference,
} from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { ZoomPreferenceType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";

function ResourceSettingsMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <IconButton onClick={onClick} aria-label="Resource Settings Menu">
      <SettingsIcon width={16} height={16} color="var(--black-tint-70)" />
    </IconButton>
  );
}

function ResourceSettingsMenu({
  editVerb,
  showZoomSettings = false,
}: {
  editVerb: "Draw" | "Edit";
  showZoomSettings?: boolean;
}) {
  const dispatch = useAppDispatch();
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProperties(currentUserId, [
    "whiteboard_font_size_preference",
    "whiteboard_stroke_size_preference",
    "zoom_preference",
  ]);

  const enableStudentDrawing = () => {
    dispatch(setEnableStudentDrawing({ isEnabled: true, userId: currentUserId }));
  };

  const disableStudentDrawing = () => {
    dispatch(setEnableStudentDrawing({ isEnabled: false, userId: currentUserId }));
  };

  const setWhiteboardStrokeSizePreference = (size: string) => () => {
    dispatch(setUserWhiteboardStrokeSizePreference({ userId: currentUserId, size }));
  };

  const setWhiteboardFontSizePreference = (size: string) => () => {
    dispatch(setUserWhiteboardFontSizePreference({ userId: currentUserId, size }));
  };

  const setZoomPreference = (zoomPreference: ZoomPreferenceType) => () => {
    dispatch(setUserZoomPreference({ userId: currentUserId, zoomPreference }));
  };

  if (!canControlClassroom && !showZoomSettings) return null;
  if (!currentUser) {
    return null;
  }

  return (
    <Menu
      tooltip="Resource Settings"
      IconComponent={SettingsIcon}
      MenuButtonComponent={ResourceSettingsMenuButton}
      title="Resource Settings"
      ariaLabel="Resource Settings Menu"
    >
      {canControlClassroom && (
        <>
          <MenuItemHeader text="Permission" />
          <MenuItem
            text={`Students Can ${editVerb}`}
            onClick={enableStudentDrawing}
            isSelected={studentsCanDraw}
            padUnselected
          />
          <MenuItem
            text={`Students Cannot ${editVerb}`}
            onClick={disableStudentDrawing}
            isSelected={!studentsCanDraw}
            padUnselected
          />
        </>
      )}
      {canControlClassroom && editVerb === "Draw" && (
        <>
          <MenuItemHeader text="Default Draw Stroke Size" />
          <div className="flex">
            {["s", "m", "l", "xl"].map((size, index) => (
              <MenuItem
                key={size}
                text={size.toUpperCase()}
                onClick={setWhiteboardStrokeSizePreference(size)}
                isSelected={currentUser.whiteboard_stroke_size_preference === size}
                padUnselected
              />
            ))}
          </div>
          <MenuItemHeader text="Default Font Size" />
          <div className="flex">
            {["s", "m", "l", "xl"].map((size, index) => (
              <MenuItem
                key={size}
                text={size.toUpperCase()}
                onClick={setWhiteboardFontSizePreference(size)}
                isSelected={currentUser.whiteboard_font_size_preference === size}
                padUnselected
              />
            ))}
          </div>
        </>
      )}
      {showZoomSettings && (
        <>
          <MenuItemHeader text="Display" />
          <MenuItem
            text="Entire Page"
            onClick={setZoomPreference(ZoomPreferenceType.ZoomToFit)}
            isSelected={currentUser.zoom_preference === ZoomPreferenceType.ZoomToFit}
            padUnselected
          />
          <MenuItem
            text="Zoom-to-Fill"
            onClick={setZoomPreference(ZoomPreferenceType.ZoomToFill)}
            isSelected={currentUser.zoom_preference === ZoomPreferenceType.ZoomToFill}
            padUnselected
          />
        </>
      )}
    </Menu>
  );
}

export { ResourceSettingsMenuButton, ResourceSettingsMenu };
